export type DeepRequired<T> = Required<{
    [K in keyof T]: T[K] extends Required<T[K]> ? T[K] : DeepRequired<T[K]>;
}>;

export interface CourseProps {
    id: string;
    courseStatus: LearningActivityStatus;
    schedule: Schedule;
    registrationStatus?: RegistrationStatus;
    transcriptStatus?: TranscriptStatus;
    transcriptId?: string;
    classroomUrl?: string;
    meetingLink?: string;
    evaluationStatus?: EvaluationStatus;
    evaluationUrl?: string;
    courseDetails: CourseDetails;
    activityName?: string;
    activityModality: string;
    location: string;
    waitlistEnabled?: WaitlistEnabled;
    isProxy: boolean;
    commercialPrivateCourseOrgIds?: string[];
    teamIds?: string[];
    isPartnerPublicUnlistedClass: boolean;
    studentNotes?: string;
    preWorkInstruction?: string;
    program?: string;
    vIltType?: string;
    learningGoalIds?: string[];
    contentConfigs?: ContentConfig[];
}

/**
 * This interface is for course details that are not tied to a specific session.
 * This is data like course name, description etc.
 * Other data like schedule, classroomURL should not be placed here.
 */
export interface CourseDetails {
    catalogItemId: string; // Unversioned ID from CatalogService
    name: string; // Name of the course e.g. Architecting on AWS
    description: string; // Description of the course.
    duration?: string; // Formatted string for duration of the course e.g. 3 Days
    category: string; // Category of the course e.g. "Media Services" -- this is represented as CatalogItem.domain
    skillLevel: string; // Skill level of a course e.g. Intermediate
    objectives: string[]; // Learner objectives
    audience: string[]; // Who this course is intended for
    outline: CourseOutline; // Outline of the course e.g. Day 1 you will learn X, Y, Z
    prerequisites: string; // Prerequisites, this is just a single string due to CatalogItem schema.
}

export interface Registration {
    registrationStatus: RegistrationStatus;
    learningActivity: LearningActivity;
    registrationType: RegistrationType;
}

export interface ClassroomAssignments {
    trainingAssignmentStatus: TrainingAssignmentStatus;
    learningActivity: LearningActivity;
}

export interface Transcript {
    id: string;
    learningActivity: LearningActivity;
    transcriptStatus: TranscriptStatus;
}

export interface Evaluation {
    evaluationUrl?: string;
    evaluationStatus: EvaluationStatus;
    learningActivity: LearningActivity;
}

export interface LearningActivity {
    pk: string;
    activity_name?: string;
    activity_status: LearningActivityStatus;
    activity_modality: string;
    activity_audience: string;
    class_size?: number;
    classrooms_student_url: string;
    course_name: string;
    customers?: {
        organization?: {
            id: string;
        };
        team?: {
            id: string;
        };
        number_of_students_committed?: number;
    }[];
    delivery_language: string;
    delivery_sessions: DeliverySession[];
    delivery_address_1?: string;
    delivery_address_2?: string;
    room?: string;
    delivery_city?: string;
    delivery_state?: string;
    delivery_postal_code?: string;
    delivery_country?: string;
    delivery_timezone: string;
    lms_type?: LmsType;
    program?: string;
    registrationCounter: number;
    registrationCounterPerOrganization?: {
        organization: { id: string };
        registrationCounter: number;
    }[];
    v_ilt_id: string;
    v_ilt_type?: string;
    waitlist_enabled?: WaitlistEnabled;
    student_notes?: { description: string };
    pre_work_instruction?: { description: string };
    course_catalog_item?: CatalogItem;
    /** Delivery-language variant of course_catalog_item. Absent when the feature flag is off. */
    localized_course_catalog_item?: CatalogItem;
    course_catalog_item_limited?: CourseCatalogItemLimited;
    learning_goal_ids?: string[];
    content_configs?: ContentConfig[];
}

/**
 * Server-side resolved catalog item with limited fields.
 * Used for ADC (Dedicated Cloud) link sharing where the user is not yet part of the org.
 * Excludes outline and children — only includes fields needed for display.
 */
export interface CourseCatalogItemLimited {
    id: string;
    versionedId: string;
    name: string;
    description?: string;
    duration: {
        unit: string;
        value: number;
    };
    domain?: {
        valueDisplayString?: string;
    };
    skillLevel?: {
        valueDisplayString?: string;
    };
    objectives?: {
        values: string[];
    };
    intendedAudience?: {
        values: string[];
    };
    prerequisites?: {
        value: string;
    };
}

export enum AUDIENCE_TYPE {
    COMMERCIAL_SEMI_PRIVATE = 'Commercial Semi-Private',
    PARTNER_SEMI_PRIVATE = 'Partner Semi-Private',
    PARTNER_PUBLIC_UNLISTED = 'Partner Public Unlisted',
}

export const SEMI_PRIVATE_AUDIENCE = [AUDIENCE_TYPE.COMMERCIAL_SEMI_PRIVATE, AUDIENCE_TYPE.PARTNER_SEMI_PRIVATE];

export const PARTNER_PUBLIC_UNLISTED = [AUDIENCE_TYPE.PARTNER_PUBLIC_UNLISTED];

export interface DeliverySession {
    start_timestamp: number;
    end_timestamp: number;
}

export enum LearningActivityStatus {
    ACTIVE = 'Active',
    CANCELED = 'Canceled',
    COMPLETED = 'Completed',
}

export enum RegistrationStatus {
    CANCELED = 'CANCELED',
    REGISTERED = 'REGISTERED',
    WITHDRAWN = 'WITHDRAWN',
    WAITLISTED = 'WAITLISTED',
    UNWAITLISTED = 'UNWAITLISTED',
    NOT_REGISTERED = 'NOT_REGISTERED',
}

export enum RegistrationType {
    SELF = 'SELF',
    PROXY = 'PROXY',
}

export enum WaitlistEnabled {
    DISABLED = 'Disabled',
    AUTOMATIC = 'Automatic',
    MANUAL = 'Manual',
}

export enum TrainingAssignmentStatus {
    ASSIGNED = 'ASSIGNED',
    UNASSIGNED = 'UNASSIGNED',
}

export enum TranscriptStatus {
    ATTENDED = 'ATTENDED',
    NO_SHOW = 'NO_SHOW',
    PARTIALLY_ATTENDED = 'PARTIALLY_ATTENDED',
}

export enum ClassStatus {
    CANCELED,
    COMPLETED,
    COMPLETED_WAITLISTED,
    WAITLISTED,
    ENROLLED,
    IN_PROGRESS,
    NO_SHOW,
    ATTENDED,
    PARTIALLY_ATTENDED,
    WITHDRAWN,
}

export enum LmsType {
    Kiku = 'Kiku',
    myClass = 'myClass',
    CustomerLMS = 'CustomerLMS',
}

export enum ActivityModality {
    vILT = 'vILT',
    ILT = 'ILT',
}

export enum VILTType {
    Necto = 'Necto',
    WebEx = 'WebEx',
    PVILT = 'PVILT',
}

export enum EvaluationStatus {
    NOT_REQUIRED = 'NOT_REQUIRED',
    INCOMPLETE = 'INCOMPLETE',
    COMPLETED = 'COMPLETED',
}

export const IsProxy = {
    TRUE: true,
    FALSE: false,
};

export const VIRTUAL_ILT_LOCATION = 'Virtual Live Class';

export interface Schedule {
    classDays: ClassDay[];
    timezone: string;
    language: string;
    seatsRemaining: number;
}

export interface ClassDay {
    startDateTime: Date;
    endDateTime: Date;
}

export enum SkillLevel {
    Fundamental = 'Fundamental',
    Intermediate = 'Intermediate',
    Advanced = 'Advanced',
}

export interface Module {
    title: string;
    modulesContent: {
        header: string;
        content: string[];
    }[];
}

export type IconSize = 'small' | 'large';

export type IconProps = {
    value?: string;
    size?: IconSize;
};

export interface ContentConfig {
    content_type: string;
    access_start_timestamp?: number;
    access_end_timestamp?: number;
    related_catalog_item?: {
        id: string;
        versionedId: string;
    };
}

// Supergraph type for CatalogItem
// https://code.amazon.com/packages/AwsTcCatalogServiceInterfaces/blobs/mainline/--/src/api/schema.graphql
export interface CatalogItem {
    id: string;
    versionedId: string;
    name: string;
    description?: string;
    duration: {
        unit: string;
        value: number;
    };
    domain?: {
        valueDisplayString?: string;
    };
    skillLevel?: {
        valueDisplayString?: string;
    };
    objectives?: {
        values: string[];
    };
    intendedAudience?: {
        values: string[];
    };
    prerequisites?: {
        value: string;
    };
    outline?: string; // CatalogService expect this outline to be a stringified JSON Array, cannot be JSON Object
    children?: {
        nodes?: CatalogItem[];
    };
}

/**
 * The outline structure for courses. Basically an unbounded tree.
 * For now, our course UI will only display 1 level of module nesting.
 * So even if this has n-many depths, we will only show the first level (for now).
 */
export interface CourseOutline {
    nodes?: CourseOutlineNode[];
}

export interface CourseOutlineNode {
    header?: string; // The header of the node. This is the title of the node.
    details?: string[]; // List of details under a specific node.
    nodes?: CourseOutlineNode[]; // Additional nodes/nesting for more organization
}

export interface LearningGoal {
    name: string;
    status: string;
    source: string;
    /**
     * Activities that make up the learning goal. Same shape as any other LearningActivity, so
     * they flow through the normal course-building pipeline. Null when the downstream service
     * has no activities for the goal (it errors on the empty case; see useLearningGoalTranscriptsByUserId).
     */
    learningActivities: LearningActivity[] | null;
}

export interface LearningGoalTranscript {
    id: string;
    learningGoal: LearningGoal;
}

// Response type for the GraphQL query in /useLearningGoalTranscriptsByUserId.ts. Fetched separately
// from learningActivitiesByUserId so a failure on this federated path can't take down the main request.
export interface LearningGoalTranscriptsByUserIdResponse {
    learningGoalTranscriptsByUserId: {
        nodes: LearningGoalTranscript[];
        pageInfo?: {
            cursor: string | null;
            size: number;
        };
    };
}

// Response type for the GraphQL Query in /useLearningActivitiesByUserId.ts
export interface LearningActivitiesByUserIdResponse {
    learningActivities: LearningActivity[];
    registrationsByUserId: {
        nodes: Registration[];
    };
    classroomTrainingAssignmentsByUserId: {
        nodes: ClassroomAssignments[];
    };
    transcriptsByUserId: {
        nodes: Transcript[];
    };
    iltEvaluationsByUserId: {
        nodes: Evaluation[];
    };
}

/**
 * Unit of CatalogDuration in the catalog schema.
 *
 * The premium content group returns SECONDS for nearly everything and MINUTES for a few
 * open-ended items, but the schema allows all four, so all four are handled.
 */
export enum CatalogDurationUnit {
    DAYS = 'DAYS',
    HOURS = 'HOURS',
    MINUTES = 'MINUTES',
    SECONDS = 'SECONDS',
}

export interface CatalogDuration {
    unit?: CatalogDurationUnit;
    value: number;
}
